import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { ServiceStatisticsServiceDTO } from '../../dto/ServiceStatisticsDTO';
import { rankServices } from './periodDetail';
import { formatInt, formatMinutes, formatPercent } from './statisticsUtils';

/** Cuántos servicios se ven antes de «Ver todos». */
const VISIBLE_ROWS = 5;

interface PeriodDetailServicesProps {
    /** Los servicios y agendas del período que se mira, con sus indicadores. */
    services: ServiceStatisticsServiceDTO[];
    /** Espera media de cada servicio en todo el período de la pantalla (ya está en los datos del tablero): la «habitual». */
    usualWait: Map<number, number>;
    /** Tocar un servicio: lo abre en el mismo panel. */
    onSelect: (service: ServiceStatisticsServiceDTO) => void;
}

/**
 * «Dónde se esperó»: una fila-botón por servicio, con su nombre completo (envuelve, no se recorta), sus atenciones y cuántos
 * esperaron más de una hora, y a la derecha la espera del período, la habitual y, si es muy distinta, una etiqueta con
 * texto («+389 % vs. habitual»). Van primero los que esperaron más de lo habitual en total (minutos de más), cinco y «Ver
 * todos (n)». Tocar una fila abre ese servicio en el panel.
 * TODO(design-system): una lista de filas clickeables en un drawer no está en Novarium; se arma con las filas de la lista
 * de avisos (separador, 44 px o más), el foco teal y los tags de nivel del ranking.
 */
export default function PeriodDetailServices({ services, usualWait, onSelect }: PeriodDetailServicesProps) {
    const intl = useIntl();
    const [expanded, setExpanded] = useState(false);
    const rows = useMemo(() => rankServices(services, usualWait), [services, usualWait]);
    const shown = expanded ? rows : rows.slice(0, VISIBLE_ROWS);

    if (rows.length === 0) {
        return (
            <p className="nv-note">
                <i className="pi pi-info-circle" aria-hidden="true" />
                {intl.formatMessage({ id: 'ServicesStatisticsPeriodDetailServicesEmpty' })}
            </p>
        );
    }
    return (
        <>
            <ul className="ss-detail__services">
                {shown.map(({ service, usual, change, unusual }) => {
                    const { attentions, avgWaitMinutes, waitOver60 } = service.current;
                    const attentionsText = intl.formatMessage({ id: 'ServicesStatisticsPeriodDetailDayAttentions' },
                        { count: attentions, countText: formatInt(attentions) });
                    const usualText = usual === null ? null
                        : intl.formatMessage({ id: 'ServicesStatisticsPeriodDetailUsual' }, { value: formatMinutes(usual) });
                    return (
                        <li key={service.serviceId}>
                            <button type="button" className="ss-detail__service"
                                aria-label={intl.formatMessage({ id: 'ServicesStatisticsPeriodDetailServiceLabel' }, {
                                    name: service.serviceName, attentions: attentionsText, wait: formatMinutes(avgWaitMinutes),
                                    usual: usualText ?? intl.formatMessage({ id: 'ServicesStatisticsPeriodDetailNoUsual' }),
                                })}
                                onClick={() => onSelect(service)}>
                                <span className="ss-detail__service-main">
                                    <span className="ss-detail__service-name">{service.serviceName}</span>
                                    <span className="ss-detail__service-meta">{attentionsText}</span>
                                    {waitOver60 > 0 && (
                                        <span className="ss-detail__service-meta">
                                            {intl.formatMessage({ id: 'ServicesStatisticsPeriodDetailOver60' },
                                                { count: waitOver60, countText: formatInt(waitOver60) })}
                                        </span>
                                    )}
                                </span>
                                <span className="ss-detail__service-wait">
                                    <span className="ss-detail__service-minutes">{formatMinutes(avgWaitMinutes)}</span>
                                    {usualText && <span className="ss-detail__service-meta">{usualText}</span>}
                                    {unusual && change !== null && (
                                        <Tag className={unusual === 'err' ? 'nv-tag-err' : 'nv-tag-warn'}
                                            value={intl.formatMessage({ id: 'ServicesStatisticsPeriodDetailUnusualTag' },
                                                { value: formatPercent(change) })} />
                                    )}
                                </span>
                                <i className="pi pi-angle-right" aria-hidden="true" />
                            </button>
                        </li>
                    );
                })}
            </ul>
            {rows.length > VISIBLE_ROWS && (
                <div className="ss-detail__more">
                    <Button type="button" link aria-expanded={expanded}
                        label={intl.formatMessage({ id: expanded ? 'ServicesStatisticsPeriodDetailShowLess' : 'ServicesStatisticsPeriodDetailShowAll' },
                            { count: rows.length })}
                        onClick={() => setExpanded(open => !open)} />
                </div>
            )}
        </>
    );
}
